/**
 * Чтение карточки каталога: `GET /api/v1/vehicles/{slug}` и provenance (BE-050, BE-064).
 *
 * Список `GET /vehicles` остаётся публичным API backend, но интерфейсом не
 * используется: страницы каталога в MVP нет, витрина — результаты подбора.
 *
 * Каталог через API только читается: методов создания и изменения не существует
 * ни на backend, ни здесь (MVP_TECH_RFC §21).
 */

import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';

import type { VehicleDetailItem, VehicleProvenanceData } from './types';

/** MVP_TECH_RFC §22: публичный каталог и карточка живут в кеше 60 секунд. */
export const CATALOG_STALE_TIME_MS = 60_000;

/**
 * Карточка автомобиля по slug (MVP_PRD §18).
 *
 * Неопубликованный и несуществующий автомобиль отдают одинаковый `404`:
 * наличие черновика — внутренняя информация каталога.
 *
 * Ответ — один из трёх уровней (BE-580): свой автомобиль рабочего набора до
 * оплаты приходит `open` — объявление без аналитики, — после оплаты `full`,
 * чужой — `teaser`. Экран различает их по `access`, а не по наличию полей.
 */
export function useVehicle(slug: string, locale?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.vehicle(slug, locale),
    queryFn: ({ signal }) => {
      const search = locale ? `?${new URLSearchParams({ locale }).toString()}` : '';
      return apiFetch<VehicleDetailItem>(`/vehicles/${encodeURIComponent(slug)}${search}`, {
        signal,
      });
    },
    // Персональная карточка узнаёт slug только из результата подбора: до этого
    // запрашивать нечего, и запрос ждёт (FE-083).
    enabled,
    staleTime: CATALOG_STALE_TIME_MS,
  });
}

/**
 * Происхождение значений карточки (BE-064, MVP_PRD §17).
 *
 * Запрос уходит только при `enabled = true` — то есть при открытой модалке.
 * Загружать эти данные вместе со страницей бессмысленно: их видит меньшинство
 * посетителей, а объём сопоставим с самой карточкой.
 *
 * Идентификатор — UUID автомобиля, а не slug: endpoint принимает только id.
 */
export function useVehicleProvenance(vehicleId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.vehicleProvenance(vehicleId),
    queryFn: ({ signal }) =>
      apiFetch<VehicleProvenanceData>(`/vehicles/${encodeURIComponent(vehicleId)}/provenance`, {
        signal,
      }),
    enabled,
    staleTime: CATALOG_STALE_TIME_MS,
  });
}
